"use client";

import { useEffect, useState } from "react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import Link from "next/link";
import {
  Check,
  Star,
  MapPin,
  Clock,
  Phone,
  Mail,
  MessageCircle,
  ChevronRight,
  ChevronLeft,
  ArrowRight,
  AlertCircle,
  X,
  Compass,
  Wifi,
  Coffee,
  Bed,
  Users,
  Utensils,
  Wind,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Home,
  Calendar,
  HelpCircle,
  Activity,
  Award,
  Quote,
} from "lucide-react";
import Slider from "react-slick";
import "../styles/carousel.css";
import dynamic from "next/dynamic";
const ImageGalleryModal = dynamic(() => import("./ImageGalleryModal"), {
  ssr: false,
});
import { fetchPackageBySlug } from "@/services/package.services";
import { IPackageDetail } from "@/types/package.types";
import { useAppDispatch, useAppSelector } from "@/hooks/hooks";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { insertWeblInquiry } from "@/services/hotel.services";
import { insertBookingReference } from "@/services/resort.services";
// Removed resort service imports as package page only displays package API sections now.

interface PackageDetailPageProps {
  packageSlug: string;
  initialPkg?: IPackageDetail | null;
}

// Custom structure for parsed list content to enforce elegant typography and high contrast
const DynamicParsedHtml = ({ html }: { html: string }) => {
  return (
    <div
      className="text-slate-700 leading-relaxed font-normal text-sm space-y-3 [&_ul]:list-none [&_ul]:pl-0 [&_ul]:mt-2 [&_ul]:space-y-3 [&_li]:relative [&_li]:pl-6 [&_li::before]:content-['✓'] [&_li::before]:absolute [&_li::before]:left-0 [&_li::before]:text-emerald-600 [&_li::before]:font-bold [&_p]:mb-2"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const DynamicParsedExclusionHtml = ({ html }: { html: string }) => {
  return (
    <div
      className="text-slate-700 leading-relaxed font-normal text-sm space-y-3 [&_ul]:list-none [&_ul]:pl-0 [&_ul]:mt-2 [&_ul]:space-y-3 [&_li]:relative [&_li]:pl-6 [&_li::before]:content-['✗'] [&_li::before]:absolute [&_li::before]:left-0 [&_li::before]:text-red-500 [&_li::before]:font-bold [&_p]:mb-2"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const DynamicParsedNeutralHtml = ({ html }: { html: string }) => {
  return (
    <div
      className="text-slate-700 leading-relaxed font-normal text-sm space-y-3 [&_ul]:list-none [&_ul]:pl-0 [&_ul]:mt-2 [&_ul]:space-y-3 [&_li]:relative [&_li]:pl-6 [&_li::before]:content-['•'] [&_li::before]:absolute [&_li::before]:left-0 [&_li::before]:text-slate-400 [&_li::before]:font-bold [&_p]:mb-2"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

// Slider Arrows matching home page testimonials
function NextArrow(props: any) {
  const { onClick } = props;
  return (
    <button
      onClick={onClick}
      className="hidden sm:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg items-center justify-center hover:bg-pink-600 hover:text-white transition cursor-pointer border border-gray-100"
      aria-label="Next slide"
    >
      <ChevronRight className="w-5 h-5" />
    </button>
  );
}

// PrevArrow matching home page testimonials
function PrevArrow(props: any) {
  const { onClick } = props;
  return (
    <button
      onClick={onClick}
      className="hidden sm:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg items-center justify-center hover:bg-pink-600 hover:text-white transition cursor-pointer border border-gray-100"
      aria-label="Previous slide"
    >
      <ChevronLeft className="w-5 h-5" />
    </button>
  );
}

// Strip HTML utility matching home page testimonials text processor
const getCleanText = (htmlContent: string) => {
  if (!htmlContent) return "";
  let cleaned = htmlContent
    .replace(/<[^>]*>/g, "") // Strip HTML tags
    .replace(/\s+/g, " ") // Collapse multiple spaces
    .replace(/\s+([.,!?;:])/g, "$1") // Remove spaces before punctuation
    .trim();
  if (cleaned.length > 250) {
    cleaned = cleaned.substring(0, 250).trim() + "...";
  }
  return cleaned;
};

// Safe helper to extract and clean map URL src from iframe html
const cleanMapUrl = (mapUrlString: string) => {
  if (!mapUrlString) return "";
  if (mapUrlString.includes('src="')) {
    const match = mapUrlString.match(/src="([^"]+)"/);
    if (match && match[1]) {
      return match[1];
    }
  }
  return mapUrlString;
};

export default function PackageDetailPage({
  packageSlug,
  initialPkg = null,
}: PackageDetailPageProps) {
  const [pkg, setPkg] = useState<IPackageDetail | null>(initialPkg);
  const [loading, setLoading] = useState(!initialPkg);
  const [error, setError] = useState<string | null>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Itinerary tab selected indexes
  const [activeDayIndex, setActiveDayIndex] = useState(0);

  // Stay policies active tab state
  const [activeInfoTab, setActiveInfoTab] = useState("");

  const dispatch = useAppDispatch();
  const InquiryLoading = useAppSelector((state) => state.hotel.InquiryLoading);
  const router = useRouter();

  // Dynamic slidesCount — reads actual window.innerWidth to fix SSR + mobile breakpoint issue
  const getSlidesToShow = (max: number) => {
    if (typeof window === "undefined") return max;
    const w = window.innerWidth;
    if (w <= 640) return 1;
    if (w <= 768) return Math.min(max, 2);
    if (w <= 1024) return Math.min(max, 3);
    return max;
  };

  const [slidesCount, setSlidesCount] = useState(() => ({
    rooms: getSlidesToShow(3),
    packages: getSlidesToShow(3),
  }));

  useEffect(() => {
    const onResize = () =>
      setSlidesCount({
        rooms: getSlidesToShow(3),
        packages: getSlidesToShow(3),
      });
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const formik = useFormik({
    initialValues: {
      FirstName: "",
      EmailID: "",
      MobileNo: "",
      CheckIn: "",
      Nights: "2 Nights",
      Adults: "2",
      Child: "0",
    },
    validateOnBlur: true,
    validationSchema: Yup.object({
      FirstName: Yup.string().trim().required("Full Name is required"),
      MobileNo: Yup.string()
        .required("Mobile number is required")
        .matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits"),
      EmailID: Yup.string()
        .required("Email is required")
        .email("Invalid email format"),
      CheckIn: Yup.string().required("Check-in date is required"),
      Nights: Yup.string().required("Nights is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      const payload = {
        FirstName: values.FirstName,
        LastName: "",
        CompanyName: "",
        Address: "",
        Email: values.EmailID,
        MobileNo: String(values.MobileNo),
        NoofPerson: +values.Adults,
        NoofChild: +values.Child,
        CheckinDate: values.CheckIn,
        TypeYourTent: pkg?.Title || "",
        ResortURL: "",
        PackageURL: packageSlug || pkg?.URL || "",
        Remarks: "",
        Nights: values.Nights,
      };

      try {
        const response = await dispatch(insertWeblInquiry(payload));
        if (response.meta.requestStatus === "fulfilled") {
          resetForm();
          setTimeout(() => {
            router.push("/thankyou/");
          }, 200);
        } else {
          toast.error(
            (response.payload as any)?.message ||
              "Failed to submit inquiry. Please try again.",
          );
        }
      } catch (err) {
        console.error(err);
        toast.error("An unexpected error occurred. Please try again later.");
      }
    },
  });

  // Load Package details on mount / slug change
  useEffect(() => {
    // Skip fetch when server pre-fetched data is already available
    if (initialPkg && initialPkg.URL === packageSlug) return;

    let isMounted = true;
    async function loadPackageData() {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchPackageBySlug(packageSlug);
        if (isMounted) {
          setPkg(data);
          setActiveDayIndex(0);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(
            err?.message ||
              "Something went wrong while fetching package details.",
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }
    loadPackageData();
    return () => {
      isMounted = false;
    };
  }, [packageSlug]);

  // Helper to compile a photo gallery
  const getPackageGallery = (packageDataObj: IPackageDetail) => {
    const list: string[] = [];

    // Include the main image
    if (packageDataObj.MainImage) {
      list.push(packageDataObj.MainImage);
    }

    // Include dynamic gallery images
    if (
      packageDataObj.GalleryImages &&
      packageDataObj.GalleryImages.length > 0
    ) {
      packageDataObj.GalleryImages?.forEach((g) => {
        if (g.GalleryImage && !list.includes(g.GalleryImage)) {
          list.push(g.GalleryImage);
        }
      });
    }

    return list;
  };

  if (loading) {
    return (
      <div className="w-full min-h-screen bg-white flex flex-col items-center justify-center pt-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
        <p className="text-gray-500 font-sans text-base">
          Loading package details...
        </p>
      </div>
    );
  }

  if (error || !pkg) {
    return (
      <div className="w-full min-h-screen bg-white flex flex-col items-center justify-center pt-20 px-4 text-center">
        <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mb-4">
          <AlertCircle className="w-8 h-8" />
        </div>
        <h2 className="text-2xl font-sans font-bold text-gray-900 mb-2">
          Unable to Load Package
        </h2>
        <p className="text-gray-500 max-w-md mx-auto mb-6 font-sans">
          {error ||
            "We couldn't find the requested package details. It might have been updated or removed."}
        </p>
        <Link
          href="/"
          className="bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 text-white px-8 py-3.5 rounded-full font-bold shadow-lg hover:shadow-xl transition transform hover:scale-[1.02] cursor-pointer"
        >
          Return to Home
        </Link>
      </div>
    );
  }

  const gallery = getPackageGallery(pkg);

  // Compile active policy info tabs dynamically
  interface IInfoTab {
    id: string;
    title: string;
    content: string;
    type: "inclusion" | "exclusion" | "neutral";
    isCancellation?: boolean;
  }
  const infoTabs: IInfoTab[] = [];
  if (pkg) {
    if (pkg.Inclusion && pkg.Inclusion.trim() !== "") {
      infoTabs.push({
        id: "inclusions",
        title: "Stay Inclusions",
        content: pkg.Inclusion,
        type: "inclusion",
      });
    }
    if (pkg.Exclusion && pkg.Exclusion.trim() !== "") {
      infoTabs.push({
        id: "exclusions",
        title: "Exclusions",
        content: pkg.Exclusion,
        type: "exclusion",
      });
    }
    if (pkg.CancellationPolicy && pkg.CancellationPolicy.trim() !== "") {
      infoTabs.push({
        id: "cancellation",
        title: "Cancellation Policy",
        content: pkg.CancellationPolicy,
        type: "neutral",
        isCancellation: true,
      });
    }
    if (pkg.RefundPolicy && pkg.RefundPolicy.trim() !== "") {
      infoTabs.push({
        id: "refund",
        title: "Refund Policy",
        content: pkg.RefundPolicy,
        type: "neutral",
      });
    }
    if (pkg.Instruction && pkg.Instruction.trim() !== "") {
      infoTabs.push({
        id: "guidelines",
        title: "Travel Guidelines",
        content: pkg.Instruction,
        type: "neutral",
      });
    }
  }

  const activeTabObj =
    infoTabs.find((t) => t.id === activeInfoTab) || infoTabs[0];

  const hasItinerary =
    pkg.PackageWiseResortItinerary &&
    pkg.PackageWiseResortItinerary.length > 0 &&
    pkg.PackageWiseResortItinerary[0]?.Days &&
    pkg.PackageWiseResortItinerary[0]?.Days.length > 0;

  const displayOtherPackages =
    pkg.OtherPackageList?.filter((op) => op.IsActive && op.URL !== pkg.URL) ||
    [];

  const rooms =
    pkg.RoomCategoryList?.filter((rc: any) => rc.IsActive).map((rc: any) => ({
      Title: rc.Title,
      Description: rc.Description,
      MainImage: rc.MainImage,
    })) || [];

  // No associated resort variables needed as we only display package-specific fields now.

  return (
    <div className="min-h-screen bg-gray-50 pb-20 font-sans antialiased text-gray-800">
      <Toaster position="top-right" richColors />

      {/* ────────────────── DYNAMIC API SECTION: HERO HEADER ────────────────── */}
      <section className="relative overflow-hidden bg-gray-950 h-[450px] md:h-[550px] w-full">
        <ImageWithFallback
          src={pkg.BannerImage || pkg.MainImage || "/placeholder.png"}
          alt={pkg.Title}
          className="w-full h-full object-cover opacity-80 scale-100 hover:scale-102 transition-transform duration-700"
          priority
          sizes="100vw"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="max-w-4xl text-white pb-10">
              {pkg.ResortName && (
                <span className="inline-block bg-pink-600 text-white text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4 shadow-md">
                  {pkg.ResortName}
                </span>
              )}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-wide mb-4 text-white leading-tight drop-shadow-md">
                {pkg.Title}
              </h1>
              {pkg.MetaDescription && (
                <p className="text-gray-100 font-medium text-base sm:text-lg leading-relaxed line-clamp-3 max-w-2xl mb-6 drop-shadow-sm">
                  {pkg.MetaDescription}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-4 text-sm font-medium">
                <div className="flex items-center gap-2 text-gray-100 bg-black/35 backdrop-blur-md px-3.5 py-2 rounded-xl">
                  <MapPin className="w-4 h-4 text-pink-500" />
                  <span>Rann of Kutch, Gujarat</span>
                </div>
                <div className="flex items-center gap-2 text-gray-100 bg-black/35 backdrop-blur-md px-3.5 py-2 rounded-xl">
                  <Clock className="w-4 h-4 text-pink-500" />
                  <span>Custom Duration</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────── BREADCRUMB NAVIGATION ────────────────── */}
      <div className="bg-white border-b border-gray-100 py-4 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-xs font-semibold tracking-wider text-gray-500 uppercase">
            <Link
              href="/"
              className="hover:text-pink-600 transition-colors flex items-center gap-1 cursor-pointer"
            >
              <Home className="w-3.5 h-3.5" />
              Home
            </Link>
            <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-gray-500">
              {pkg.ResortName || "Kutch Stays"}
            </span>
            <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-gray-900 font-semibold">{pkg.Title}</span>
          </div>
        </div>
      </div>

      {/* Main Layout Grid */}
      <section className="py-20 bg-white border-b border-gray-150">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
            {/* Left Main Content */}
            <div className="lg:col-span-2 space-y-16">
              {/* PHOTO GALLERY */}
              {gallery.length > 0 && (
                <div>
                  <div className="mb-6 flex justify-between items-end border-b border-gray-100 pb-4">
                    <div className="text-left">
                      <h2 className="text-2xl text-gray-900 font-semibold mb-1">
                        Photo Gallery
                      </h2>
                      <p className="text-xs text-gray-500 font-normal">
                        Visual journey of your desert resort escape
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedImageIndex(0);
                        setGalleryOpen(true);
                      }}
                      type="button"
                      className="text-xs font-semibold text-pink-600 hover:text-pink-700 transition cursor-pointer"
                    >
                      View All ({gallery.length})
                    </button>
                  </div>

                  <div className="grid grid-cols-6 gap-3">
                    {gallery.slice(0, 5).map((image, index) => (
                      <div
                        key={index}
                        onClick={() => {
                          setSelectedImageIndex(index);
                          setGalleryOpen(true);
                        }}
                        className={`relative overflow-hidden rounded-xl cursor-pointer group bg-gray-100 border border-gray-100 ${
                          index === 0
                            ? "col-span-6 md:col-span-4 row-span-2 h-72 sm:h-80"
                            : "col-span-3 md:col-span-2 h-36"
                        }`}
                      >
                        <ImageWithFallback
                          src={image}
                          alt={`Package gallery image ${index + 1}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-all duration-300"></div>
                      </div>
                    ))}

                    {/* More Photos trigger box */}
                    {gallery.length > 5 && (
                      <button
                        onClick={() => {
                          setSelectedImageIndex(5);
                          setGalleryOpen(true);
                        }}
                        type="button"
                        className="col-span-3 md:col-span-2 h-36 bg-gray-900 relative rounded-xl overflow-hidden flex flex-col items-center justify-center group cursor-pointer text-center px-4"
                      >
                        <ImageWithFallback
                          src={gallery[5] || gallery[0]}
                          alt="Gallery remaining cover"
                          className="absolute inset-0 w-full h-full object-cover opacity-35 group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gray-950/40"></div>
                        <span className="relative text-white font-semibold text-base tracking-wide z-10">
                          +{gallery.length - 5} more photos
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* DYNAMIC API SECTION: ITINERARY TIMELINE */}
              {hasItinerary && (
                <div className="border-t border-gray-100 pt-12">
                  <div className="text-center max-w-3xl mx-auto mb-12">
                    <h2 className="text-2xl text-gray-900 mb-2 font-semibold">
                      Detailed Day-by-Day Itineraries
                    </h2>
                    <p className="text-gray-600 font-normal text-sm leading-relaxed">
                      Take a look at our carefully structured daily schedules,
                      curated to give you the perfect balance of adventure and
                      relaxation.
                    </p>
                  </div>

                  {/* Day Selector Buttons */}
                  <div className="flex border-b border-gray-100 mb-8 overflow-x-auto gap-8 justify-center scrollbar-none">
                    {pkg.PackageWiseResortItinerary![0].Days.map((day, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveDayIndex(idx)}
                        type="button"
                        className={`pb-4 text-sm font-semibold tracking-wide relative whitespace-nowrap transition cursor-pointer ${
                          activeDayIndex === idx
                            ? "text-pink-600 font-bold"
                            : "text-gray-500 hover:text-gray-800"
                        }`}
                      >
                        Day {day.Day}
                        {activeDayIndex === idx && (
                          <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-pink-600 rounded-full" />
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Timeline */}
                  <div className="space-y-6 py-2">
                    {pkg.PackageWiseResortItinerary![0].Days[
                      activeDayIndex
                    ]?.Data?.map((item, idx) => (
                      <div key={idx} className="relative group">
                        <div
                          className="text-gray-700 font-normal text-sm sm:text-base leading-relaxed select-text blog-content"
                          dangerouslySetInnerHTML={{ __html: item.Itinerary }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Sidebar - Sticky booking form card */}
            <div className="lg:col-span-1">
              <div
                id="booking-sidebar"
                className="sticky top-32 bg-white rounded-xl p-6 shadow-lg border-2 border-gray-100"
              >
                {/* Package price badge inside card */}
                {pkg.Price > 0 && (
                  <div className="mb-6 pb-6 border-b border-gray-100 text-center font-sans">
                    <span className="text-xs text-gray-400 uppercase tracking-widest block font-bold mb-1">
                      Package Starting Price
                    </span>
                    <span className="text-4xl font-extrabold text-pink-600">
                      ₹{pkg.Price.toLocaleString()}
                    </span>
                    <span className="text-xs text-gray-500 font-normal block mt-1">
                      {pkg?.PricePer || "Per Person"}
                    </span>
                  </div>
                )}

                <h3 className="text-2xl text-gray-900 mb-6 font-semibold text-center">
                  Book This Package
                </h3>

                <form onSubmit={formik.handleSubmit} className="space-y-3">
                  {/* Full Name */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Full Name *
                    </label>
                    <input
                      name="FirstName"
                      type="text"
                      placeholder="Your Full Name"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.FirstName}
                      className={`w-full px-4 py-3 border-2 ${
                        formik.touched.FirstName && formik.errors.FirstName
                          ? "border-red-500 focus:ring-red-500/20"
                          : "border-gray-200 focus:border-pink-500 focus:ring-pink-500/20"
                      } rounded-xl outline-none transition-all duration-300 bg-white text-sm text-gray-800 font-normal`}
                    />
                    {formik.touched.FirstName && formik.errors.FirstName && (
                      <span className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        {formik.errors.FirstName as string}
                      </span>
                    )}
                  </div>

                  {/* Email ID */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Email Address *
                    </label>
                    <input
                      name="EmailID"
                      type="email"
                      placeholder="your@email.com"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.EmailID}
                      className={`w-full px-4 py-3 border-2 ${
                        formik.touched.EmailID && formik.errors.EmailID
                          ? "border-red-500 focus:ring-red-500/20"
                          : "border-gray-200 focus:border-pink-500 focus:ring-pink-500/20"
                      } rounded-xl outline-none transition-all duration-300 bg-white text-sm text-gray-800 font-normal`}
                    />
                    {formik.touched.EmailID && formik.errors.EmailID && (
                      <span className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        {formik.errors.EmailID as string}
                      </span>
                    )}
                  </div>

                  {/* Phone Number */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Phone Number *
                    </label>
                    <input
                      name="MobileNo"
                      type="tel"
                      maxLength={10}
                      placeholder="10-digit mobile no"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.MobileNo}
                      className={`w-full px-4 py-3 border-2 ${
                        formik.touched.MobileNo && formik.errors.MobileNo
                          ? "border-red-500 focus:ring-red-500/20"
                          : "border-gray-200 focus:border-pink-500 focus:ring-pink-500/20"
                      } rounded-xl outline-none transition-all duration-300 bg-white text-sm text-gray-800 font-normal`}
                    />
                    {formik.touched.MobileNo && formik.errors.MobileNo && (
                      <span className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        {formik.errors.MobileNo as string}
                      </span>
                    )}
                  </div>

                  {/* Check In Date */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Travel Date *
                    </label>
                    <input
                      name="CheckIn"
                      type="date"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.CheckIn}
                      className={`w-full px-4 py-3 border-2 ${
                        formik.touched.CheckIn && formik.errors.CheckIn
                          ? "border-red-500 focus:ring-red-500/20"
                          : "border-gray-200 focus:border-pink-500 focus:ring-pink-500/20"
                      } rounded-xl outline-none bg-white text-sm text-gray-800 font-normal`}
                      min={new Date().toISOString().split("T")[0]}
                    />
                    {formik.touched.CheckIn && formik.errors.CheckIn && (
                      <span className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        {formik.errors.CheckIn as string}
                      </span>
                    )}
                  </div>

                  {/* Nights */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Nights *
                    </label>
                    <select
                      name="Nights"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.Nights}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-pink-500 outline-none bg-white text-sm text-gray-800 font-normal cursor-pointer"
                    >
                      <option value="1 Nights">1 Nights</option>
                      <option value="2 Nights">2 Nights</option>
                      <option value="3 Nights">3 Nights</option>
                    </select>
                  </div>

                  {/* Adults & Child grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Adults *
                      </label>
                      <select
                        name="Adults"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.Adults}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-pink-500 outline-none bg-white text-sm text-gray-800 font-normal cursor-pointer"
                      >
                        {Array.from({ length: 20 }, (_, i) => i + 1).map(
                          (n) => (
                            <option key={n} value={String(n)}>
                              {n} {n === 1 ? "Adult" : "Adults"}
                            </option>
                          ),
                        )}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Child
                      </label>
                      <select
                        name="Child"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.Child}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-pink-500 outline-none bg-white text-sm text-gray-800 font-normal cursor-pointer"
                      >
                        {Array.from({ length: 21 }, (_, i) => i).map((n) => (
                          <option key={n} value={String(n)}>
                            {n} {n === 1 ? "Child" : "Children"}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Submit Trigger */}
                  <button
                    type="submit"
                    disabled={formik.isSubmitting || InquiryLoading}
                    className="w-full bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 text-white px-6 py-4 rounded-xl hover:shadow-xl hover:scale-[1.02] active:scale-95 transition-all duration-300 cursor-pointer font-bold text-sm flex items-center justify-center gap-2"
                  >
                    {formik.isSubmitting || InquiryLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                        <span>Sending Inquiry...</span>
                      </>
                    ) : (
                      <span>Send Package Inquiry</span>
                    )}
                  </button>
                  {pkg.BookingURL && (
                    <button
                      type="button"
                      onClick={() => {
                        insertBookingReference(0, window.location.href).catch(
                          () => {},
                        );
                        window.open(
                          pkg.BookingURL,
                          "_blank",
                          "noopener,noreferrer",
                        );
                      }}
                      className="w-full border-2 border-pink-500 text-pink-600 px-6 py-4 rounded-xl hover:shadow-xl hover:scale-[1.02] active:scale-95 transition-all duration-300 cursor-pointer font-bold text-sm flex items-center justify-center gap-2"
                    >
                      Book Now
                    </button>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────── DYNAMIC API SECTION: ROOM TYPES SLIDER (FLAT) ────────────────── */}
      {rooms.length > 0 && (
        <section className="py-20 bg-gray-50/30 border-b border-gray-150 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="text-2xl text-gray-900 mb-2 font-semibold">
                Room Types & Pricing
              </h2>
              <p className="text-gray-600 font-normal text-sm">
                Explore our luxurious accommodation options beautifully
                integrated with local style and modern premium amenities.
              </p>
            </div>

            <div className="relative px-0 sm:px-8">
              <Slider
                {...{
                  dots: true,
                  infinite: rooms.length > slidesCount.rooms,
                  speed: 500,
                  slidesToShow: slidesCount.rooms,
                  slidesToScroll: 1,
                  autoplay: rooms.length > slidesCount.rooms,
                  autoplaySpeed: 5000,
                  swipe: true,
                  touchMove: true,
                  arrows: slidesCount.rooms > 1 && rooms.length > 1,
                  nextArrow:
                    slidesCount.rooms > 1 && rooms.length > 1 ? (
                      <NextArrow />
                    ) : undefined,
                  prevArrow:
                    slidesCount.rooms > 1 && rooms.length > 1 ? (
                      <PrevArrow />
                    ) : undefined,
                }}
              >
                {rooms.map((room, index) => (
                  <div key={index} className="px-1 sm:px-3 pb-4">
                    <div className="group rounded-xl border border-gray-200 bg-white overflow-hidden flex flex-col justify-between hover:border-pink-200 hover:shadow-lg transition-all duration-300 h-[410px]">
                      <div>
                        <div className="h-56 overflow-hidden bg-gray-100 relative">
                          <ImageWithFallback
                            src={room.MainImage || "/placeholder.png"}
                            alt={room.Title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-5 flex-grow flex flex-col justify-between">
                          <div>
                            <h4 className="text-xl font-semibold text-gray-800 mb-3 group-hover:text-pink-600 transition-colors">
                              {room.Title}
                            </h4>
                            <div
                              className="text-xs font-normal text-gray-600 leading-relaxed line-clamp-5"
                              dangerouslySetInnerHTML={{
                                __html: room.Description || "",
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </Slider>
            </div>
          </div>
        </section>
      )}

      {/* ────────────────── DYNAMIC API SECTION: STAY POLICIES & GUIDELINES TABBED CONTAINER ────────────────── */}
      {infoTabs.length > 0 && (
        <section className="py-20 bg-white border-b border-gray-150">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Tabs Header bar */}
            <div className="flex border-b border-gray-200 overflow-x-auto gap-2 sm:gap-6 mb-8 scrollbar-none justify-center">
              {infoTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveInfoTab(tab.id)}
                  type="button"
                  className={`pb-4 text-xs sm:text-sm font-semibold tracking-wider relative whitespace-nowrap transition cursor-pointer uppercase ${
                    activeInfoTab === tab.id ||
                    (!activeInfoTab && tab.id === infoTabs[0].id)
                      ? "text-pink-600 font-bold"
                      : "text-gray-500 hover:text-gray-800"
                  }`}
                >
                  {tab.title}
                  {(activeInfoTab === tab.id ||
                    (!activeInfoTab && tab.id === infoTabs[0].id)) && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-pink-600 rounded-full" />
                  )}
                </button>
              ))}
            </div>

            {/* Active Tab Panel Content */}
            {activeTabObj && (
              <div
                className={`p-6 sm:p-10 rounded-2xl border transition-all duration-300 ${
                  activeTabObj.isCancellation
                    ? "bg-amber-50/10 border-amber-200/50 shadow-sm"
                    : "bg-gray-50 border-gray-100 shadow-sm"
                }`}
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-6 tracking-wide">
                  {activeTabObj.title}
                </h3>
                <div className="prose max-w-none">
                  {activeTabObj.type === "inclusion" ? (
                    <DynamicParsedHtml html={activeTabObj.content} />
                  ) : activeTabObj.type === "exclusion" ? (
                    <DynamicParsedExclusionHtml html={activeTabObj.content} />
                  ) : (
                    <DynamicParsedNeutralHtml html={activeTabObj.content} />
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ────────────────── DYNAMIC API SECTION: INTERACTIVE LOCATION MAP ────────────────── */}
      {pkg.MapURL && pkg.MapURL.trim() !== "" && (
        <section className="py-20 bg-gray-50/50 border-b border-gray-150">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="text-2xl text-gray-900 mb-2 font-semibold text-center">
                Interactive Location Map
              </h2>
              <p className="text-gray-600 font-normal text-sm leading-relaxed text-center">
                Find the exact geographical coordinates and plan your navigation
                seamlessly.
              </p>
            </div>

            <div className="w-full h-[300px] rounded-xl overflow-hidden shadow-md border border-gray-200 bg-gray-150">
              <iframe
                src={cleanMapUrl(pkg.MapURL)}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen={true}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={`${pkg.Title} Location Map`}
              ></iframe>
            </div>
          </div>
        </section>
      )}

      {/* DYNAMIC API SECTION: OTHER PACKAGES SLIDER */}
      {displayOtherPackages.length > 0 && (
        <section className="py-20 bg-gray-50/50 border-t border-gray-150 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="text-2xl text-gray-900 mb-2 font-semibold">
                Other Featured Packages
              </h2>
              <p className="text-gray-600 font-normal text-sm leading-relaxed">
                Explore our other highly rated glamping package options designed
                for your perfect Kutchi escape.
              </p>
            </div>

            <div className="relative px-0 sm:px-8">
              <Slider
                {...{
                  dots: true,
                  infinite: displayOtherPackages.length > slidesCount.packages,
                  speed: 500,
                  slidesToShow: slidesCount.packages,
                  slidesToScroll: 1,
                  autoplay: displayOtherPackages.length > slidesCount.packages,
                  autoplaySpeed: 5000,
                  swipe: true,
                  touchMove: true,
                  arrows:
                    slidesCount.packages > 1 && displayOtherPackages.length > 1,
                  nextArrow:
                    slidesCount.packages > 1 &&
                    displayOtherPackages.length > 1 ? (
                      <NextArrow />
                    ) : undefined,
                  prevArrow:
                    slidesCount.packages > 1 &&
                    displayOtherPackages.length > 1 ? (
                      <PrevArrow />
                    ) : undefined,
                }}
              >
                {displayOtherPackages.map((other, index) => (
                  <div key={index} className="px-1 sm:px-3 pb-4">
                    <Link
                      href={`/package/${other.URL}`}
                      className="group block bg-white rounded-2xl border border-gray-200 overflow-hidden hover:border-pink-200 hover:shadow-lg transition-all duration-300 h-[380px]"
                    >
                      <div className="h-48 overflow-hidden bg-gray-100 relative">
                        <ImageWithFallback
                          src={
                            other.MainImage ||
                            "https://images.unsplash.com/photo-1762254970300-f1bc796c1ed8?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=600"
                          }
                          alt={other.Title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5 flex flex-col justify-between h-[180px]">
                        <h4 className="text-lg font-semibold text-gray-800 group-hover:text-pink-600 transition-colors line-clamp-2 leading-snug">
                          {other.Title}
                        </h4>
                        <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-auto">
                          <div>
                            <span className="text-xl font-bold text-gray-950">
                              ₹{other.Price.toLocaleString()}
                            </span>
                            <span className="text-xs text-gray-400 block mt-0.5">
                              {other?.PricePer || "Per Person"}
                            </span>
                          </div>
                          <span className="text-xs font-bold text-pink-600 uppercase tracking-wider group-hover:underline">
                            View Package →
                          </span>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              </Slider>
            </div>
          </div>
        </section>
      )}

      {/* GALLERY MODAL */}
      <ImageGalleryModal
        images={gallery}
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        initialIndex={selectedImageIndex}
      />
    </div>
  );
}
